import {
  Animation,
  type ArcRotateCamera,
  CubicEase,
  EasingFunction,
  Vector3,
} from '@babylonjs/core';

// This is the lowest FPS we can have that allows us to have 2x shorter animations if needed.
// 2 frames in the base case, 1 frame if 2x shorter:
// 2.5 / 2 * 0.8 = 1 (obviously can't have less than 1 FPS - then the transition happens instantly).
// Seems counterintuitive, but the animations don't look choppy even at such low FPS.
const ANIMATION_FPS = 2.5;
const ANIMATION_DURATION_SECONDS = 0.8;

const animateCameraProperties = (
  camera: ArcRotateCamera,
  animationConfig: Partial<Record<keyof ArcRotateCamera, unknown>>,
  onAnimationEnd?: () => void,
  easingMode = EasingFunction.EASINGMODE_EASEINOUT,
  speedMultiplier = 1,
): void => {
  const ease = new CubicEase();
  ease.setEasingMode(easingMode);

  const totalFrames = Math.max(
    1,
    Math.round((ANIMATION_DURATION_SECONDS / speedMultiplier) * ANIMATION_FPS),
  );

  const animations = Object.entries(animationConfig).map(
    ([property, value]) => {
      const animation = new Animation(
        `Camera_${property}`,
        property,
        ANIMATION_FPS,
        value instanceof Vector3 ?
          Animation.ANIMATIONTYPE_VECTOR3
        : Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT,
      );

      animation.setKeys([
        {
          frame: 0,
          value: camera[property as keyof ArcRotateCamera] as number | Vector3,
        },
        { frame: totalFrames, value },
      ]);

      animation.setEasingFunction(ease);

      return animation;
    },
  );

  camera
    .getScene()
    .beginDirectAnimation(camera, animations, 0, totalFrames, false, 1, () => {
      if (onAnimationEnd) onAnimationEnd();
    });
};

const animateRadius = (
  camera: ArcRotateCamera,
  targetRadius: number,
  onAnimationEnd?: () => void,
  speedMultiplier = 1,
): void => {
  const ease = new CubicEase();
  ease.setEasingMode(EasingFunction.EASINGMODE_EASEOUT);

  const totalFrames = Math.max(
    1,
    Math.round((ANIMATION_DURATION_SECONDS / speedMultiplier) * ANIMATION_FPS),
  );

  Animation.CreateAndStartAnimation(
    'Camera_radius',
    camera,
    'radius',
    ANIMATION_FPS,
    totalFrames,
    camera.radius,
    targetRadius,
    Animation.ANIMATIONLOOPMODE_CONSTANT,
    ease,
    onAnimationEnd,
  );
};

export const DEFAULT_GLOBAL_CAMERA_CONFIG = {
  target: Vector3.Zero(),
  radius: 1,
  fov: 0.35,
  upVector: new Vector3(0, 1, 0),
  position: new Vector3(0, 0, 1),
};

export type GlobalCameraConfig = typeof DEFAULT_GLOBAL_CAMERA_CONFIG;

export type CameraState = {
  target: Vector3 | null;
  radius: number | null;
  fov: number | null;
  upVector: Vector3 | null;
  hasRestoredOriginal: boolean;
};

export const transitionToGlobalView = (
  camera: ArcRotateCamera,
  transitionRadiusAfterOtherProperties: boolean,
  {
    onAnimationEnd,
    globalCameraConfig = DEFAULT_GLOBAL_CAMERA_CONFIG,
  }: Partial<{
    onAnimationEnd: () => void;
    globalCameraConfig: GlobalCameraConfig;
  }> = {},
): void => {
  const transitionConfigWithoutRadius = {
    // For the global camera, we want it to look like it's rotating exactly around the object, so use the center as target.
    target: DEFAULT_GLOBAL_CAMERA_CONFIG.target,
    fov: globalCameraConfig.fov,
    upVector: globalCameraConfig.upVector,
  };

  if (transitionRadiusAfterOtherProperties) {
    // When transitioning radius, there are 2 animations, but we want the duration to be the same.
    const animationSpeedMultiplier = 2;

    animateCameraProperties(
      camera,
      transitionConfigWithoutRadius,
      (): void => {
        animateRadius(
          camera,
          globalCameraConfig.radius,
          onAnimationEnd,
          animationSpeedMultiplier,
        );
      },
      EasingFunction.EASINGMODE_EASEIN,
      animationSpeedMultiplier,
    );
  } else {
    animateCameraProperties(
      camera,
      { ...transitionConfigWithoutRadius, radius: globalCameraConfig.radius },
      onAnimationEnd,
    );
  }
};

export const transitionToCamera = (
  camera: ArcRotateCamera,
  targetProperties: {
    target: Vector3;
    position: Vector3;
    radius: number;
    fov: number;
  },
  onAnimationEnd?: () => void,
): void => {
  // Only animate the radius if it's not super close to the current.
  const shouldTransitionRadius =
    Math.abs(camera.radius - targetProperties.radius) > 0.001;

  animateCameraProperties(
    camera,
    {
      target: targetProperties.target,
      position: targetProperties.position,
      fov: targetProperties.fov,
    },
    () => {
      if (shouldTransitionRadius) {
        animateRadius(camera, targetProperties.radius, onAnimationEnd);
      } else if (onAnimationEnd) {
        onAnimationEnd();
      }
    },
    EasingFunction.EASINGMODE_EASEINOUT,
  );
};
